import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';

const AVATAR_COLORS = ['#6366f1','#0891b2','#059669','#d97706','#e11d48','#7c3aed','#0284c7','#16a34a'];

function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function initials(name) {
  const parts = (name || '').trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (name || '?')[0].toUpperCase();
}

export default function SuperAdminUsers() {
  const isAdmin = localStorage.getItem('is_admin') === 'true';
  const myEmail = localStorage.getItem('username'); // username is email at login
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | inactive
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('users/superadmin/users/')
      .then(r => setUsers(r.data.users))
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const filtered = useMemo(() => {
    let list = users;
    if (filter === 'active') list = list.filter(u => u.is_active);
    if (filter === 'inactive') list = list.filter(u => !u.is_active);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(u =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.department || '').toLowerCase().includes(q) ||
        (u.designation || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [users, filter, search]);

  const toggleActive = async (u) => {
    setToggling(u.id);
    try {
      const r = await api.post(`users/superadmin/users/${u.id}/toggle-active/`);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_active: r.data.is_active } : x));
    } catch {
      alert('Failed to update user status.');
    } finally {
      setToggling(null);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
        Access restricted to administrators.
      </div>
    );
  }

  const activeCount = users.filter(u => u.is_active).length;
  const inactiveCount = users.length - activeCount;

  return (
    <div style={{ maxWidth: 960, margin: '32px auto', padding: '0 20px' }}>

      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>User Management</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
          Activate or deactivate user accounts across the platform.
        </p>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'All users', count: users.length, value: 'all', bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
          { label: 'Active', count: activeCount, value: 'active', bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
          { label: 'Inactive', count: inactiveCount, value: 'inactive', bg: '#fff1f2', color: '#e11d48', border: '#fecdd3' },
        ].map(c => (
          <button key={c.value} onClick={() => setFilter(c.value)} style={{
            padding: '8px 18px', borderRadius: 99, border: `1.5px solid ${filter === c.value ? c.color : c.border}`,
            background: filter === c.value ? c.color : c.bg,
            color: filter === c.value ? '#fff' : c.color,
            fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            transition: 'all .15s',
          }}>
            {c.label}
            <span style={{
              background: filter === c.value ? 'rgba(255,255,255,.25)' : c.color,
              color: filter === c.value ? '#fff' : '#fff',
              borderRadius: 99, padding: '1px 8px', fontSize: 11, fontWeight: 800,
            }}>{c.count}</span>
          </button>
        ))}

        {/* Search */}
        <div style={{ marginLeft: 'auto', position: 'relative' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, email, dept…"
            style={{
              padding: '8px 14px 8px 36px', borderRadius: 8, border: '1.5px solid #e2e8f0',
              fontSize: 13, color: '#0f172a', background: '#fff', outline: 'none', width: 220,
            }}
          />
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: '#94a3b8' }}>🔍</span>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>Loading users…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#e11d48', fontSize: 14 }}>{error}</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>No users found.</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 180px 160px 110px 120px',
            padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
          }}>
            {['User', 'Department', 'Designation', 'Status', 'Action'].map(h => (
              <span key={h} style={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.6px' }}>
                {h}
              </span>
            ))}
          </div>

          {/* Rows */}
          {filtered.map((u, i) => {
                const isSelf = myEmail === u.email;
            const color = avatarColor(u.name || u.username);
            return (
              <div key={u.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 180px 160px 110px 120px',
                padding: '14px 20px', alignItems: 'center',
                borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none',
                background: !u.is_active ? '#fafafa' : '#fff',
                opacity: !u.is_active ? 0.75 : 1,
              }}>
                {/* User info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%', background: color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>
                    {initials(u.name || u.username)}
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {u.name || u.username}
                      {u.is_admin && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: '#7c3aed', color: '#fff', padding: '1px 7px', borderRadius: 99 }}>
                          Admin
                        </span>
                      )}
                      {isSelf && (
                        <span style={{ fontSize: 10, color: '#94a3b8' }}>(you)</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 1 }}>{u.email}</div>
                  </div>
                </div>

                {/* Department */}
                <div style={{ fontSize: 13, color: '#374151' }}>{u.department || '—'}</div>

                {/* Designation */}
                <div style={{ fontSize: 13, color: '#374151' }}>{u.designation || '—'}</div>

                {/* Status chip */}
                <div>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '3px 10px', borderRadius: 99, fontSize: 11.5, fontWeight: 600,
                    background: u.is_active ? '#f0fdf4' : '#fff1f2',
                    color: u.is_active ? '#16a34a' : '#e11d48',
                    border: `1px solid ${u.is_active ? '#bbf7d0' : '#fecdd3'}`,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: u.is_active ? '#16a34a' : '#e11d48', display: 'inline-block' }} />
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Toggle button */}
                <div>
                  {isSelf ? (
                    <span style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
                  ) : (
                    <button
                      disabled={toggling === u.id}
                      onClick={() => toggleActive(u)}
                      style={{
                        padding: '5px 14px', borderRadius: 7, border: 'none', cursor: toggling === u.id ? 'wait' : 'pointer',
                        fontSize: 12, fontWeight: 700,
                        background: u.is_active ? '#fff1f2' : '#f0fdf4',
                        color: u.is_active ? '#e11d48' : '#16a34a',
                        opacity: toggling === u.id ? 0.6 : 1,
                        transition: 'all .15s',
                      }}
                    >
                      {toggling === u.id ? '…' : u.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ marginTop: 16, fontSize: 11, color: '#cbd5e1', textAlign: 'center' }}>
        Inactive users cannot log in. Changes take effect immediately.
      </p>
    </div>
  );
}
